export const AnalyticsEvents = {
  // Acquisition
  LANDING_CTA_CLICKED: 'landing_cta_clicked',
  LANDING_DOOR_CLICKED: 'landing_door_clicked',
  LANDING_SECTION_VIEWED: 'landing_section_viewed',
  SIGNUP_STARTED: 'signup_started',
  // Funnel step, not the signup count: the email form's completion in the browser (consent-gated)
  // and, for a new OAuth account, the server's copy (BOO-5156, same distinct_id + uuid as the
  // client's, so PostHog collapses the pair). Use it for started -> completed conversion by
  // `method`. To COUNT signups use `user_signed_up`. See docs/operations/signup-events.md.
  SIGNUP_COMPLETED: 'signup_completed',
  // CANONICAL signup event: server-side, one per account (deterministic uuid), email + OAuth, and
  // the only one that does not depend on the browser's consent state. Count signups with this.
  USER_SIGNED_UP: 'user_signed_up',
  // Signup-page OAuth authentication succeeds for both new and existing users.
  OAUTH_SIGNUP_AUTHENTICATED: 'oauth_signup_authenticated',
  // Server-observed OAuth round trip (`/api/auth/callback`): one `oauth_callback` per return with
  // an `outcome`; `oauth_error` adds a bounded `stage` + `error_code` (never a message). Both are
  // anonymous. `oauth_return_without_callback` is the browser's own view of an abandoned round trip
  // (the user came back to the sign-up form without a callback, e.g. Back from Google).
  OAUTH_CALLBACK: 'oauth_callback',
  OAUTH_ERROR: 'oauth_error',
  OAUTH_RETURN_WITHOUT_CALLBACK: 'oauth_return_without_callback',
  // An OAuth signup from an invite link: one per flow, `outcome` from a closed set (server:
  // redeemed, carrier_missing, not_new_account, role_mismatch, unavailable, refused_after_check,
  // config_missing, failed, unknown; browser: carrier_unavailable) plus `method`. Never the code.
  OAUTH_INVITE_OUTCOME: 'oauth_invite_outcome',
  SIGNUP_ABANDONED: 'signup_abandoned',
  SIGNUP_VALIDATION_ERROR: 'signup_validation_error',
  SIGNUP_ERROR: 'signup_error',
  SIGNUP_FORM_VIEWED: 'signup_form_viewed',
  // Field-level funnel (BOO-4892). Properties are the field NAME and a boolean only,
  // never the value: { field, entity_type, has_invite_code, completed? }.
  SIGNUP_FIELD_FOCUSED: 'signup_field_focused',
  SIGNUP_FIELD_COMPLETED: 'signup_field_completed',
  // Auth-form telemetry (BOO-5163). Field names and validity KEYS only, never values.
  AUTH_FIELD_FOCUSED: 'auth_field_focused',
  AUTH_FIELD_INVALID: 'auth_field_invalid',
  AUTH_FORM_SUBMITTED: 'auth_form_submitted',
  AUTH_SUBMIT_FAILED: 'auth_submit_failed',
  AUTH_FORM_ABANDONED: 'auth_form_abandoned',
  AUTH_FORM_RESUMED: 'auth_form_resumed',
  SIGNUP_ENTITY_TYPE_SELECTED: 'signup_entity_type_selected',
  INVITE_CODE_VIEWED: 'invite_code_viewed',
  INVITE_CODE_SUBMITTED: 'invite_code_submitted',
  INVITE_CODE_REDEEMED: 'invite_code_redeemed',

  // Auth Flow
  EMAIL_VERIFICATION_VIEWED: 'email_verification_viewed',
  EMAIL_VERIFIED: 'email_verified',
  LOGIN_COMPLETED: 'login_completed',
  LOGIN_FAILED: 'login_failed',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  WAITING_APPROVAL_VIEWED: 'waiting_approval_viewed',

  // Onboarding
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_STEP_BACK: 'onboarding_step_back',
  ONBOARDING_VALIDATION_ERROR: 'onboarding_validation_error',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_COMPLETED_BACKEND: 'onboarding_completed_backend',
  ONBOARDING_ABANDONED: 'onboarding_abandoned',
  // Post-onboarding "first value" choice screen (BOO-2964/BOO-2965). Fired with
  // { action: 'chat' | 'bank' | 'ksef' | 'upload' } when the user picks their first action.
  ONBOARDING_FIRST_ACTION_SELECTED: 'onboarding_first_action_selected',
  // BOO-3381 "+ Add company" second-entity flow. Deliberately distinct from the
  // ONBOARDING_* funnel events so multi-entity adds never contaminate the
  // first-user acquisition funnel or its A/B experiments.
  ADDITIONAL_COMPANY_FLOW_STARTED: 'additional_company_flow_started',
  ADDITIONAL_COMPANY_CREATED: 'additional_company_created',
  REGISTRY_SYNC_STARTED: 'registry_sync_started',
  REGISTRY_SYNC_COMPLETED: 'registry_sync_completed',
  REGISTRY_SYNC_FAILED: 'registry_sync_failed',
  WELCOME_MESSAGE_VIEWED: 'welcome_message_viewed',

  // Activation
  FIRST_MESSAGE_SENT: 'first_message_sent',
  FIRST_TOOL_RESULT_VIEWED: 'first_tool_result_viewed',
  FIRST_DOCUMENT_UPLOADED: 'first_document_uploaded',
  FIRST_CALCULATION_RUN: 'first_calculation_run',
  // Browser: at least 2 of the 4 first-time milestones above (BOO-2965). Renamed from
  // `activation_completed` on 2026-10-28 so that name is free for the canonical, server-side event.
  ONBOARDING_MILESTONES_REACHED: 'onboarding_milestones_reached',
  // CANONICAL activation, emitted by the backend only (`services/server_product_events.py`): the
  // account has at least one document AND at least one invoice. No browser code emits it.
  ACTIVATION_COMPLETED: 'activation_completed',

  // Core Product Usage
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_RESPONSE_RECEIVED: 'chat_response_received',
  TOOL_RESULT_VIEWED: 'tool_result_viewed',
  TOOL_EXECUTED: 'tool_executed',
  DOCUMENT_UPLOADED: 'document_uploaded',
  INVOICE_CREATED: 'invoice_created',
  INVOICE_DOWNLOADED: 'invoice_downloaded',
  CALCULATOR_VIEWED: 'calculator_viewed',
  CALCULATOR_USED: 'calculator_used',
  // First edit after load, once per session (JDG slice 2: result-first calculators).
  CALCULATOR_INPUT_CHANGED: 'calculator_input_changed',
  DEMO_VIDEO_OPENED: 'demo_video_opened',
  DEMO_VIDEO_PLAYED: 'demo_video_played',
  DEMO_VIDEO_COMPLETED: 'demo_video_completed',
  DEMO_VIDEO_CTA_CLICKED: 'demo_video_cta_clicked',
  // Rename-ready public-calculator funnel placeholders (B2B 2026 page).
  // Do not alias these to ACTIVATION_COMPLETED — that event stays undefined
  // for anonymous calculator traffic.
  CALCULATOR_CTA_CLICKED: 'calculator_cta_clicked',
  CALCULATOR_EXAMPLE_LOADED: 'calculator_example_loaded',
  REPORT_GENERATED: 'report_generated',
  ARTIFACT_VIEWED: 'artifact_viewed',
  THREAD_CREATED: 'thread_created',
  THREAD_DELETED: 'thread_deleted',
  // U13 (audit U2/F1): a chat run that fails — server-side run error, a
  // rejected/failed HITL resume, or a pre-flight connection healthcheck
  // failure — previously left zero PostHog signal, so a real user's
  // backend-outage run deaths were only diagnosable by asking them to
  // reload. Fired with { has_thread, error_class, phase: 'run' | 'resume' |
  // 'connection' } — error_class is a status-family/exception-class bucket
  // only, never the raw error message or any PII.
  CHAT_RUN_FAILED: 'chat_run_failed',
  LLM_CALL_COMPLETED: 'llm_call_completed',
  // Deployment warm-up (#8080 scale-to-zero): a fire-and-forget GET /api/ok
  // sent so the LangGraph deployment starts before the user's first send.
  // Fired with { app: 'frontend' | 'accounting', trigger: 'chat_open' |
  // 'login' | 'visible' | 'keepalive' } once per ping actually sent (a
  // throttled ping fires nothing). No PII.
  DEPLOYMENT_WARMUP_SENT: 'deployment_warmup_sent',
  // ms from chat-open (the chat_open warm-up moment) to the first run that
  // chat starts, one value per chat-open. Fired with { app, ms, warmup: 'sent'
  // | 'throttled' | 'at_send', kind: 'message' | 'resume' }; never message
  // content. `resume` is an answered approval card (JDG only). Until the
  // first-send follow-up to #8119 the JDG app recorded composer messages only
  // and no `kind`: do not compare numbers across that change.
  CHAT_FIRST_SEND_MS: 'chat_first_send_ms',
  BANK_STATEMENT_VIEW_OPENED: 'bank_statement_view_opened',
  BANK_STATEMENT_CORRECTION_ATTEMPTED: 'bank_statement_correction_attempted',

  // Content & Marketing
  BLOG_CTA_CLICKED: 'blog_cta_clicked',
  BLOG_POST_READ: 'blog_post_read',
  READ_WITH_AI_CLICKED: 'read_with_ai_clicked',
  NEWSLETTER_SIGNUP: 'newsletter_signup',
  NEWSLETTER_SUBSCRIPTION_RECORDED: 'newsletter_subscription_recorded',
  NEWSLETTER_SUBSCRIPTION_CONFIRMED: 'newsletter_subscription_confirmed',
  NEWSLETTER_UNSUBSCRIBED: 'newsletter_unsubscribed',
  INVITE_CODE_CREATED: 'invite_code_created',

  // AI Feedback
  AI_FEEDBACK_GIVEN: 'ai_feedback_given',
  CONVERSATION_CSAT_GIVEN: 'conversation_csat_given',
  SUGGESTED_PROMPT_CLICKED: 'suggested_prompt_clicked',

  // Engagement
  SCROLL_DEPTH_REACHED: 'scroll_depth_reached',
  SEARCH_PERFORMED: 'search_performed',
  HELP_REQUESTED: 'help_requested',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
  // Consent funnel events. `cookie_consent_banner_shown` fires when the
  // prompt becomes visible with `source: auto | programmatic`; first-time
  // declines are captured before PostHog restores its prior consent state.
  COOKIE_CONSENT_BANNER_SHOWN: 'cookie_consent_banner_shown',
  COOKIE_CONSENT_GIVEN: 'cookie_consent_given',
  // Separate replay consent (BOO-5163): property `granted` (boolean). Only sent when analytics were accepted.
  REPLAY_CONSENT_CHANGED: 'replay_consent_changed',
  COOKIE_CONSENT_DECLINED: 'cookie_consent_declined',
  COOKIE_CONSENT_WITHDRAWN: 'cookie_consent_withdrawn',
  WIZARD_SESSION_RESTORED: 'wizard_session_restored',
  MICRO_TOOL_USED: 'micro_tool_used',
  QUIZ_STEP_COMPLETED: 'quiz_step_completed',
  // Onboarding quiz (BOO-5309): one event per step, properties are `step` (1-based), `step_name`
  // and `answered` (boolean). The answer itself is zero-party data and never goes to analytics.
  ONBOARDING_QUIZ_STEP: 'onboarding_quiz_step',
  // Combined result screen (BOO-3491). `best_form` is the calculator's own label key, never an answer.
  ONBOARDING_QUIZ_RESULT_VIEWED: 'onboarding_quiz_result_viewed',
  ONBOARDING_QUIZ_RESULT_SAVED: 'onboarding_quiz_result_saved',
  // Social-proof interlude shown between steps (BOO-5309). `verified` is false for every placeholder.
  ONBOARDING_QUIZ_INTERLUDE: 'onboarding_quiz_interlude',

  // Substrate-v2 — deepagents-native UI activity.
  // Fired once per ``SubAgentIndicator`` mount cycle (per active subagent
  // name) so we can measure how often the breadcrumb is visible to users.
  SUBSTRATE_ACTIVITY_VISIBLE: 'substrate_activity_visible',

  // KIS interpretation hub (www.bookeper.pl/interpretacje, apps/hub). Properties are
  // tier/topic/placement labels only: never a NIP, ruling text or amount.
  HUB_RULING_VIEWED: 'hub_ruling_viewed',
  HUB_TOPIC_VIEWED: 'hub_topic_viewed',
  HUB_NIP_BOX_SUBMITTED: 'hub_nip_box_submitted',
} as const;

export type AnalyticsEvent = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];
